import { useForm } from 'react-hook-form';

// Hooks
import { DUPLICATE_NAME, useDuplicateDashboard } from '@/hooks/dashboard/useDuplicateDashboard';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import PropTypes from 'prop-types';

import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Label from '@/components/base/Label';
// Components
import Modal from '@/components/base/Modal';

const EditNameForm = ({ toggle, isLoading, onConfirm, onError, isEditing, btnLabel }) => {
    const { t } = useTranslation('commons');
    const { duplicateDashboardData, dispatch } = useDuplicateDashboard();

    const {
        register,
        handleSubmit,
        setError,
        formState: { errors, isSubmitting }
    } = useForm({
        defaultValues: {
            name: duplicateDashboardData.name
        }
    });

    const onSubmit = async ({ name }) => {
        try {
            await onConfirm(name);
            dispatch({
                type: DUPLICATE_NAME,
                payload: {
                    name
                }
            });
        } catch (e) {
            setError('name', {
                type: 'manual',
                message: e?.response?.data?.errors?.name
                    ? e.response.data.errors.name
                          .map((errorName) => t(`errors:error_dashboard.name.${errorName}`))
                          .join(' ')
                    : t('errors:error_occured_submit_data')
            });
            if (typeof onError === 'function') {
                onError();
            }
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="rounded-md bg-icoGray-100 p-8 dark:bg-icoDarkMode-deepBlack">
                <Label required htmlFor="first_name">
                    {isEditing ? t('dashboard:edit_name_label') : t('dashboard:new_name_label')}
                </Label>
                <Input
                    id="name"
                    className="bg-transparent"
                    placeholder={t('dashboard:dashboard_name')}
                    type="text"
                    error={errors?.name?.message}
                    {...register('name', {
                        required: t('errors:field_required')
                    })}
                />
            </div>

            <div className="mt-8 flex items-center justify-between pb-12">
                <button
                    type="button"
                    onClick={toggle}
                    className="font-GSemiBold text-xs text-icoGray-500 underline dark:text-icoDarkMode-wolf">
                    {t('cancel')}
                </button>
                <Button type="submit" isLoading={isSubmitting || isLoading}>
                    {btnLabel || t('next')}
                </Button>
            </div>
        </form>
    );
};

EditNameForm.propTypes = {
    isLoading: PropTypes.bool,
    toggle: PropTypes.func,
    onConfirm: PropTypes.func,
    onError: PropTypes.func,
    btnLabel: PropTypes.string
};

export const EditNameModal = ({
    isOpen,
    isLoading,
    toggle,
    onConfirm,
    onError,
    title,
    isEditing = false,
    btnLabel = undefined
}) => {
    const { t } = useTranslation('dashboard');

    return (
        <Modal open={isOpen} onDismiss={toggle}>
            <Modal.Body>
                <Modal.Title>{title ?? t('create_new_dashboard')}</Modal.Title>
                <Modal.Content className="pt-8 text-sm">
                    <EditNameForm
                        toggle={toggle}
                        isLoading={isLoading}
                        onConfirm={onConfirm}
                        onError={onError}
                        isEditing={isEditing}
                        btnLabel={btnLabel}
                    />
                </Modal.Content>
            </Modal.Body>
        </Modal>
    );
};

EditNameModal.propTypes = {
    isOpen: PropTypes.bool,
    isLoading: PropTypes.bool,
    toggle: PropTypes.func,
    onConfirm: PropTypes.func,
    onError: PropTypes.func,
    title: PropTypes.string,
    btnLabel: PropTypes.string
};
